'use client';

/**
 * FlowTime — циферблат и управление таймером.
 *
 * Кольцо рисуется через `stroke-dasharray`, а не набором сегментов:
 * так прогресс двигается непрерывно и не требует перерисовки разметки
 * каждую секунду.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Pause,
  Play,
  RotateCcw,
  SkipForward,
  TriangleAlert,
  ChevronDown,
  Plus,
  Wind,
} from 'lucide-react';
import { Link } from '@/i18n/routing';
import type { FlowTimeApi } from '@/components/datahunter/use-flowtime';
import { isBreak } from '@/components/datahunter/use-flowtime';
import type { TimerMode } from '@/lib/flowtime-storage';
import { fmtClock } from '@/lib/flowtime-format';

const MODES: TimerMode[] = ['focus', 'short', 'long', 'free'];

const RADIUS = 132;
const RING = 2 * Math.PI * RADIUS;

export function FlowTimer({ flow }: { flow: FlowTimeApi }) {
  const t = useTranslations('dh_flowtime_ui');
  const { session, mode, running, active, remainingMs, elapsedMs, progress, settings } = flow;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const task = flow.taskById(session?.taskId ?? null);
  const project = flow.projectById(task?.projectId ?? null);
  const activeTasks = useMemo(
    () => flow.data.tasks.filter((x) => !x.done).sort((a, b) => a.order - b.order),
    [flow.data.tasks],
  );

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, [menuOpen]);

  const shownMs = mode === 'free' ? elapsedMs : remainingMs;
  const modeFull = t(`mode_${mode}_full`);

  const subtitle = running
    ? mode === 'focus'
      ? (task?.title ?? t('state_focusing'))
      : modeFull
    : active
      ? `${t('state_paused')} · ${modeFull}`
      : mode === 'free'
        ? t('state_free')
        : isBreak(mode)
          ? t('state_break')
          : (task?.title ?? t('state_ready'));

  // Точки цикла: сколько помидоров осталось до длинного перерыва.
  const cycleDots = Array.from({ length: settings.longEvery }, (_, i) => i < flow.data.cyclePos);

  return (
    <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 sm:p-8 flex flex-col items-center min-w-0">
      {/* Режимы.
          Четыре кнопки в ряд задают карточке минимальную ширину около
          390px. На экране в 360px из-за этого разъезжалась вся страница,
          а циферблат, считающий размер от карточки, вылезал за край.
          `max-w-full` не даёт ряду распирать родителя. */}
      <div
        className="inline-flex max-w-full overflow-x-auto rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] p-1"
        role="group"
        aria-label={t('timer_group')}
      >
        {MODES.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => {
              flow.setMode(m);
            }}
            aria-pressed={mode === m}
            className={`shrink-0 px-3 sm:px-4 py-1.5 rounded-full text-sm transition-colors ${
              mode === m
                ? 'bg-[var(--color-text)] text-[var(--color-bg)] font-medium'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            {t(`mode_${m}`)}
          </button>
        ))}
      </div>

      {/* Циферблат */}
      <div className="relative w-full max-w-[340px] aspect-square my-7">
        <svg viewBox="0 0 300 300" className="w-full h-full -rotate-90" aria-hidden="true">
          <circle
            cx="150"
            cy="150"
            r={RADIUS}
            fill="none"
            stroke="var(--color-bg-elevated)"
            strokeWidth="10"
          />
          <circle
            cx="150"
            cy="150"
            r={RADIUS}
            fill="none"
            stroke="var(--color-text)"
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={RING}
            strokeDashoffset={RING * (1 - progress)}
            // Круглый наконечник при нуле выглядит как точка на пустом
            // кольце, поэтому линия появляется только с началом отсчёта.
            opacity={progress > 0.0005 ? 1 : 0}
            style={{ transition: 'stroke-dashoffset 480ms linear, opacity 200ms' }}
          />
          {/* Минутные риски: дают циферблату масштаб. */}
          {Array.from({ length: 60 }, (_, i) => {
            const a = (i / 60) * Math.PI * 2;
            const long = i % 5 === 0;
            const r1 = long ? 112 : 116;
            return (
              <line
                key={i}
                x1={150 + r1 * Math.cos(a)}
                y1={150 + r1 * Math.sin(a)}
                x2={150 + 120 * Math.cos(a)}
                y2={150 + 120 * Math.sin(a)}
                stroke="var(--color-border)"
                strokeWidth={long ? 2 : 1.2}
              />
            );
          })}
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 pointer-events-none">
          <div
            className="font-mono font-semibold tabular-nums tracking-tight leading-none"
            style={{ fontSize: 'clamp(2.75rem, 11vw, 4rem)' }}
          >
            {fmtClock(shownMs / 1000)}
          </div>
          <p className="text-sm text-[var(--color-text-muted)] max-w-[70%] text-center truncate">
            {subtitle}
          </p>
          <div
            className="flex gap-1.5 mt-2"
            title={t('cycle_hint', { done: flow.data.cyclePos, total: settings.longEvery })}
          >
            {cycleDots.map((on, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  on ? 'bg-[var(--color-text)]' : 'bg-[var(--color-bg-elevated)]'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Выбор задачи */}
      <div className="relative w-full max-w-[380px]" ref={menuRef}>
        <button
          type="button"
          onClick={() => {
            setMenuOpen(!menuOpen);
          }}
          className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] hover:border-[var(--color-text-muted)] transition-colors"
          aria-expanded={menuOpen}
          aria-haspopup="listbox"
        >
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: project?.color ?? 'var(--color-text-muted)' }}
          />
          <span className="flex-1 text-left text-sm truncate">{task?.title ?? t('no_task')}</span>
          <ChevronDown className="w-4 h-4 text-[var(--color-text-muted)] shrink-0" />
        </button>

        {menuOpen && (
          <div
            className="absolute top-full left-0 right-0 mt-1.5 z-30 max-h-72 overflow-y-auto p-1.5 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-2xl"
            role="listbox"
          >
            <button
              type="button"
              onClick={() => {
                flow.setTask(null);
                setMenuOpen(false);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left hover:bg-[var(--color-bg-elevated)] transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-[var(--color-text-muted)] shrink-0" />
              {t('no_task')}
            </button>

            {activeTasks.map((task2) => {
              const p = flow.projectById(task2.projectId);
              return (
                <button
                  key={task2.id}
                  type="button"
                  onClick={() => {
                    flow.setTask(task2.id);
                    setMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors hover:bg-[var(--color-bg-elevated)] ${
                    task2.id === session?.taskId ? 'bg-[var(--color-bg-elevated)]' : ''
                  }`}
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: p?.color ?? 'var(--color-text-muted)' }}
                  />
                  <span className="flex-1 truncate">{task2.title}</span>
                  <span className="text-xs text-[var(--color-text-muted)] tabular-nums shrink-0">
                    {flow.taskPomos(task2.id)}
                    {task2.estimate > 0 ? `/${String(task2.estimate)}` : ''}
                  </span>
                </button>
              );
            })}

            {activeTasks.length === 0 && (
              <p className="px-3 py-2 text-sm text-[var(--color-text-muted)]">
                {t('no_tasks_yet')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Управление */}
      <div className="flex items-center gap-3.5 mt-6">
        <button
          type="button"
          onClick={() => {
            flow.reset(elapsedMs < 60_000);
          }}
          disabled={!active}
          title={t('btn_reset_hint')}
          aria-label={t('btn_reset')}
          className="w-11 h-11 grid place-items-center rounded-full border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-text-muted)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <RotateCcw className="w-4.5 h-4.5" />
        </button>

        <button
          type="button"
          onClick={flow.toggle}
          disabled={running && settings.strict}
          title={running && settings.strict ? t('btn_play_strict') : t('btn_play_hint')}
          className="dh-cta inline-flex items-center gap-2.5 h-14 min-w-[168px] px-8 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {running ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 fill-current" />}
          {running ? t('btn_pause') : active ? t('btn_resume') : t('btn_start')}
        </button>

        <button
          type="button"
          onClick={flow.skip}
          title={t('btn_next_hint')}
          aria-label={t('btn_next')}
          className="w-11 h-11 grid place-items-center rounded-full border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-text-muted)] transition-colors"
        >
          <SkipForward className="w-4.5 h-4.5" />
        </button>
      </div>

      {/* Быстрые действия */}
      <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
        {/* Дыхание предлагается только на перерыве.

            Перерыв — единственный момент, когда это уместно: посреди
            рабочего интервала такая кнопка звала бы уйти со страницы
            ровно тогда, когда человек просил помочь ему не отвлекаться.

            Обычная ссылка, а не переход в обработчике: открывается в
            новой вкладке средним щелчком, и таймер продолжает идти в
            этой. */}
        {isBreak(mode) && (
          <Link
            href="/app/breathing-exercise"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--color-border)] text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
          >
            <Wind className="w-3.5 h-3.5" />
            {t('btn_breathe')}
          </Link>
        )}

        <button
          type="button"
          onClick={flow.addInterruption}
          disabled={!active}
          title={t('btn_interrupt_hint')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            (session?.interruptions ?? 0) > 0
              ? 'border-[var(--color-warning-500)] text-[var(--color-warning-400)]'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
          }`}
        >
          <TriangleAlert className="w-3.5 h-3.5" />
          {t('btn_interrupt')}
          <span className="tabular-nums font-medium">{session?.interruptions ?? 0}</span>
        </button>

        <button
          type="button"
          onClick={() => {
            flow.addMinutes(5);
          }}
          disabled={mode === 'free'}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[var(--color-border)] text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5" />
          {t('btn_add_minutes')}
        </button>
      </div>

      <input
        type="text"
        value={session?.note ?? ''}
        onChange={(e) => {
          flow.setNote(e.target.value);
        }}
        placeholder={t('note_placeholder')}
        maxLength={500}
        className="mt-5 w-full max-w-[380px] px-4 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-sm placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-accent-500)] transition-colors"
      />
    </section>
  );
}
